<template>
  <f7-page :page-content="true">
    <f7-navbar :back-link="$t('atras')">
      <!-- Quien solo entra por la tarjeta de vacaciones no ve una ficha de empleado, así que
           tampoco se le titula como tal. -->
      <f7-nav-title v-if="soloTarjetaVacaciones" sliding>{{$t('vacaciones.titulo')}}</f7-nav-title>
      <f7-nav-title v-else-if="id && !readonly" sliding>{{$t('empleados.editar')}}</f7-nav-title>
      <f7-nav-title v-else-if="id" sliding>{{$t('empleados.empleado')}}</f7-nav-title>
      <f7-nav-title v-else sliding>{{$t('empleados.nuevo')}}</f7-nav-title>
      <f7-nav-right>
        <!-- Guardar solo cuando la ficha está montada: sin `!soloTarjetaVacaciones`, entrar por
             /empleados/edit/:id/ con el respaldo de roles y recibir después un menú que no trae
             empleados dejaba el botón sobre un formulario que ya no existe, y pulsarlo reventaba
             en `$refs.rform` (revisión #1711). -->
        <f7-button
          v-if="!readonly && !soloTarjetaVacaciones"
          fill
          icon-f7="floppy_disk"
          text="Guardar"
          class="display-flex"
          @click="$refs.rform.saveItem()"
        ></f7-button>
        <!-- Editar y eliminar empleados cuelga del permiso del módulo, el mismo que autoriza
             /empleados/edit/:id/, y lo resuelve la barrera de la ruta con el usuario de esta
             carga (`puedeGestionar`). Antes iba por el rol a secas y respondía distinto que la
             ruta en los dos sentidos (ver `permisos.js`).
             DIRECTOR, que llega aquí por la tarjeta de vacaciones, sigue sin poder guardar ni
             borrar el empleado: no tiene el módulo. -->
        <template slot="right" v-else-if="puedeGestionarEmpleado">
          <f7-button color="blue" @click="readonly=false">Editar</f7-button>
          <f7-button v-if="puedeEliminar" color="red" @click="deleteItem">Eliminar</f7-button>
          <!-- [#932] Eliminar la ficha de quien tiene usuario de aplicación le quita el acceso, y
               eso es solo de ADMIN. No se esconde ni se queda mudo: se ve apagado y, al pulsarlo,
               dice por qué, quién lo hace y qué hacer si lo que se quería era la baja (§9). -->
          <f7-button
            v-else
            color="gray"
            icon-f7="lock"
            v-tooltip="$t('empleados.acceso.soloAdminEliminar')"
            @click="explicarEliminar"
          >Eliminar</f7-button>
        </template>
      </f7-nav-right>
    </f7-navbar>
    <!-- [revisión #1711] La ficha del empleado solo la monta quien puede verla. A DIRECTOR se le
         abrió esta ruta para la tarjeta de vacaciones —que la API le deja ver de cualquiera—, y
         montarla entera le enseñaba además correo, teléfono, DNI, firma, altas y bajas y los
         datos de organización, que antes no veía. `editable: false` impedía guardarlos, pero lo
         que sobraba era verlos. -->
    <r-form
      v-if="!soloTarjetaVacaciones"
      ref="rform"
      v-slot="{rform}"
      :itemId="id"
      @save="onSave"
      @loadItem="onLoadEmpleado"
      :readonly="readonly"
      :dbAdapter="Empleado"
      :beforeSave="beforeSaveEmpleado"
      :documentoAdapter="Documento"
    >
      <f7-card class="padding-vertical-half padding-right" style="z-index: 3000">
        <f7-card-content>
          <f7-row no-gap class="list list-form no-hairlines">
            <f7-col width="100" medium="50">
              <ul>
                <r-input
                  class="col-100 medium-50"
                  floating-label
                  :label="$t('empleados.username')"
                  :form="rform"
                  type="text"
                  name="username"
                ></r-input>
                <r-input
                  class="col-100 medium-50"
                  floating-label
                  :label="$t('empleados.nombre')"
                  :form="rform"
                  type="text"
                  name="nombre"
                ></r-input>
                <r-input
                  class="col-100 medium-50"
                  floating-label
                  :label="$t('empleados.email')"
                  :form="rform"
                  type="text"
                  name="email"
                ></r-input>
                <r-input
                  class="col-100 medium-50"
                  floating-label
                  :label="$t('empleados.telefono')"
                  :form="rform"
                  type="text"
                  name="telefono"
                ></r-input>
                <!-- [#932] El rol y el usuario de aplicación son el acceso de la persona a la ERP:
                     solo ADMIN los cambia, y la API rechaza el guardado de cualquier otro que los
                     toque. Sin el permiso se quedan a la vista pero de solo lectura. -->
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="Rol"
                  floating-label 
                  :label="$t('empleados.rol')"
                  :form="rform"
                  name="rol"
                  idProp="valor"
                  labelProp="nombre"
                  :readonly="!puedeCambiarAcceso"
                ></r-autocomplete> 
                <r-input
                  class="col-25 medium-25"
                  :label="$t('empleados.crearuser')"
                  :form="rform"
                  type="check"
                  name="createdUser"
                  :readonly="!puedeCambiarAcceso"
                ></r-input>
                <r-input
                  class="col-25 medium-25"
                  :label="$t('empleados.activo')"
                  :form="rform"
                  type="check"
                  name="activo"
                ></r-input>
                <!-- [#932] El porqué, junto a los dos controles que apaga y en su propia línea, para
                     que nada de lo que hay alrededor cambie de sitio. Solo mientras se edita: en la
                     consulta no hay nada que se pueda tocar. -->
                <li v-if="!readonly && !puedeCambiarAcceso" class="col-100 aviso-acceso-erp">
                  {{ id ? $t('empleados.acceso.soloAdminEdicion') : $t('empleados.acceso.soloAdminAlta') }}
                </li>
                <r-files
                  class="col-100 medium-50 margin-top"
                  :form="rform"
                  name="firma"
                  :documentoAdapter="Documento"
                  label="Añadir Firma"
                  :maxFiles="1"
                ></r-files>
              </ul>
            </f7-col>
            <f7-col width="100" medium="50">
              <ul>
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="Empresa"
                  floating-label
                  :label="$t('empleados.codEmpresa')"
                  :form="rform"
                  name="datos.codEmpresa"
                  idProp="codigo"
                  idFieldDetail="id"
                  prefixProp="codigo"
                  labelProp="nombre"
                ></r-autocomplete>
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="LineaNegocio"
                  floating-label
                  :label="$t('empleados.codLineaNegocio')"
                  :form="rform"
                  name="datos.codLineaNegocio"
                  idProp="codigo"
                  prefixProp="codigo"
                  idFieldDetail="id"
                  labelProp="nombre"
                ></r-autocomplete>
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="Delegacion"
                  floating-label
                  :label="$t('empleados.codDelegacion')"
                  :form="rform"
                  name="datos.codDelegacion"
                  idProp="codigo"
                  prefixProp="codigo"
                  idFieldDetail="id"
                  labelProp="nombre"
                ></r-autocomplete>
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="CentroTrabajo"
                  floating-label
                  :label="$t('empleados.codCentroTrabajo')"
                  :form="rform"
                  name="datos.codCentroTrabajo"
                  idProp="codigo"
                  prefixProp="codigo"
                  idFieldDetail="id"
                  labelProp="nombre"
                ></r-autocomplete>
                <r-autocomplete
                  class="col-100 medium-50"
                  :dbAdapter="Departamento"
                  floating-label
                  :label="$t('empleados.codDepartamento')"
                  :form="rform"
                  name="datos.codDepartamento"
                  idProp="codigo"
                  prefixProp="codigo"
                  idFieldDetail="id"
                  labelProp="nombre"
                ></r-autocomplete>
                <r-input
                  class="col-25 medium-25"
                  floating-label
                  :label="$t('empleados.fecha_alta')"
                  :form="rform"
                  type="datepicker"
                  :name="'fechaAlta'"
                  :calendar-params="{
                    closeOnSelect: true,
                    routableModals: false,
                    dateFormat: {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    },
                  }"
                />
                <r-input
                  class="col-25 medium-25"
                  floating-label
                  :label="$t('empleados.fecha_baja')"
                  :form="rform"
                  type="datepicker"
                  :name="'fechaBaja'"
                  :calendar-params="{
                    closeOnSelect: true,
                    routableModals: false,
                    dateFormat: {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    },
                  }"
                />
                <r-input
                  class="col-25 medium-25"
                  floating-label
                  :label="$t('empleados.dni')"
                  :form="rform"
                  name="dni"
                />
                <!-- enlace para mostrar popup de colegas -->
                <f7-col class="col-100 medium-100">
                  <r-autocomplete-m2m
                    :dbAdapter="Empleado"
                    floating-label
                    outline
                    :label="$t('empleados.colegas')"
                    icon-f7="person_badge_plus"
                    :form="rform"
                    name="colegas"
                    idProp="username"
                    labelProp="nombre"
                    prefixProp="username"
                    type="textarea"
                    :lineBreak="true"
                    :viewTooltip="false"
                  ></r-autocomplete-m2m>
                </f7-col>






              </ul>
            </f7-col>
          </f7-row>
        </f7-card-content>
      </f7-card>
      <f7-card class="padding-vertical-half padding-right" style="z-index: 2000" v-if="rform.formData.rol && rform.formData.rol == 'INSPECTOR'"> 
        <f7-card-content>
          <f7-row no-gap class="list list-form no-hairlines">
            <r-autocomplete
              outline
              :dbAdapter="Oficina"
              class="col-100 medium-25"
              floating-label
              :label="$t('empleados.oficina')"
              :form="rform"
              :shownFields="['direccion','telefono','email']"
              :idProp="null"
              name="oficina"
              labelProp="nombre"
            ></r-autocomplete>
            <r-input
              outline
              class="col-100 medium-25"
              floating-label
              :label="$t('empleados.auditoria_manual')"
              :form="rform"
              type="number"
              name="percentilAuditoria"
            ></r-input>
            <r-autocomplete-m2m
              outline
              :dbAdapter="Servicio"
              class="col-100 medium-50"
              floating-label
              :label="$t('empleados.servicios')"
              :form="rform"
              name="servicios"
              labelProp="nombre"
              type="textarea"
              prefixProp="codigo"
              :lineBreak="true"
              :viewTooltip="false"
            ></r-autocomplete-m2m>
            <!-- Códigos huérfanos de esa misma lista (#329): entradas cuyo código no existe en el
                 catálogo de servicios. Va aquí, pegado al campo «Servicios» y no en la cabecera de
                 la pantalla, porque es donde se decide qué puede hacer este inspector. Ocupa una
                 línea propia (col-100) a propósito: los controles que hay encima y debajo se
                 quedan donde estaban, que es lo que pide la instrucción permanente de dirección.
                 Va contra `usernameFicha` —el username ya guardado— y no contra el campo del
                 formulario, igual que las demás tarjetas: el username se edita letra a letra y,
                 atado al campo, preguntaría por los huérfanos de nombres intermedios o los de
                 otro usuario que sí exista. -->
            <servicios-huerfanos-aviso
              v-if="usernameFicha"
              ref="huerfanosAviso"
              :username="usernameFicha"
            ></servicios-huerfanos-aviso>
            <!-- Y el aviso del NÚMERO de servicios marcados (#305), que no es lo mismo que los
                 huérfanos: ahí el problema es un código que no existe, aquí es una lista entera que
                 no se parece a la del resto de la plantilla. Sale del guardado —lo contesta la
                 respuesta de guardar el empleado— y no de una consulta, así que no se dibuja al
                 abrir la ficha: aparece cuando alguien acaba de guardarla, que es cuando el aviso
                 sirve de algo. También ocupa su línea para no mover ningún control de su sitio. -->
            <aviso-servicios-marcados :avisos="avisosServicios"></aviso-servicios-marcados>
            <r-input
              outline
              class="col-100 medium-33"
              floating-label
              label="Dirección inicio jornada"
              :form="rform"
              name="direccionInicio"
            ></r-input>
            <r-timepicker
              class="col-100 medium-33"
              outline
              floating-label
              label="Hora inicio jornada"
              :form="rform"
              name="horaInicio"
              :clearButton="false"
            ></r-timepicker>
            <r-input
              outline
              class="col-100 medium-33"
              floating-label
              label="Objetivo Diario"
              :form="rform"
              type="number"
              name="objetivoDiario"
            ></r-input>
          </f7-row>
        </f7-card-content>
      </f7-card>
      <f7-card class="padding-vertical-half padding-right" style="z-index: 2000" v-else-if="rform.formData.rol && rform.formData.rol == 'CLIENTE'"> 
        <f7-card-content>
          <f7-row no-gap class="list list-form no-hairlines">
            <f7-block-title class="col-100">
              Clientes asociados
              <f7-link v-if="!readonly" @click="addToArray('clientes')">{{$t('ofertas.add')}}</f7-link>
            </f7-block-title>
            <div class="form-caja margin-horizontal col-100 medium-50">
              <div
                class="col-100"
                v-for="(cliente, index) in rform.formData.clientes"
                :key="'clienteasociado-' + index"
              >
                <r-autocomplete
                  :dbAdapter="Cliente"
                  class="col-100 medium-50"
                  floating-label
                  :label="$t('ofertas.cliente')"
                  :form="rform"
                  :name="'clientes.' + index"
                  labelProp="codigoNombre"
                  :minLength="3"
                  :shownFields="['codigo', 'razonSocial', 'activo', 'codigoNombre']"
                  :readonly="readonly"
                  :async="true"
                  :clear-button="false"
                  :filter="{filter:{activo: true}}"
                >
                  <f7-link
                    v-if="!readonly"
                    v-tooltip="'Borrar'"
                    slot="inner-end"
                    @click="$delete(rform.formData.clientes, index);"
                    class="input-suffix-btn"
                    icon-f7="trash"
                    style="top: initial;right: 15px;"
                  ></f7-link>
                </r-autocomplete>
              </div>
            </div>
          </f7-row>
        </f7-card-content>
      </f7-card>
      <!-- Domicilio y calendario laboral (#53). Vive fuera del guardado de la ficha: tiene su
           propio endpoint y su propio botón, porque nada de esto se guarda en EmpleadoMO (I5)
           y un save() completo del empleado lo borraría.
           Va contra `usernameFicha`, no contra el campo del formulario: el username se
           edita letra a letra y, atado al campo, la tarjeta pediría —o guardaría— el domicilio
           de nombres intermedios, o el de otro usuario que sí exista. Solo cambia cuando el
           empleado se carga o se guarda, que es cuando ese usuario existe de verdad.
           Le pasamos el `readonly` de la ficha: en /empleados/view/:id/ la ruta entra con
           editable:false y el resto del formulario queda bloqueado, pero la tarjeta decidía su
           edición solo con el permiso del servidor, así que un ADMIN podía guardar el domicilio
           desde la vista de «Ver» (revisión CODEX del PR #1709). -->
      <rh-domicilio-card
        v-if="usernameFicha"
        ref="domicilioCard"
        :username="usernameFicha"
        :readonly="readonly"
      ></rh-domicilio-card>
      <!-- [#864] Perfil de CRM. Dentro del r-form porque se guarda con la ficha —es un campo suyo y
           el servidor decide en el mismo guardado si quien guarda puede cambiarlo—, y al final para
           que ningún control de los de antes cambie de sitio. -->
      <perfil-crm-card
        ref="perfilCrmCard"
        :form="rform"
        :empleadoId="empleadoIdFicha"
        :readonly="readonly"
      ></perfil-crm-card>
      <pre style="display:none;">{{rform.formData}}</pre>
  </r-form>
  <!-- Fuera del r-form del empleado a propósito: la tarjeta guarda en rh_ficha_vacaciones con su
       propio endpoint, así que guardar la ficha o sincronizar con SAP no la toca (decisión I5). -->
  <!-- `@guardada`: el check «Gestiona vacaciones en este usuario» de esta tarjeta decide en qué
       usuario se gestionan las funciones de la persona —y moverlo las traslada con él (#51)— y
       también cuál de sus cuentas manda sobre el domicilio y el calendario
       (`RhCuentasPersona.porMando`). Las dos tarjetas tienen que volver a preguntar. -->
  <vacaciones-card
    v-if="id && usernameFicha"
    :username="usernameFicha"
    @guardada="recargarPorCuentaGestora"
  ></vacaciones-card>
  <!-- Puesto y funciones (#51). Por lo mismo: colección propia `rh_empleado_funcion` y endpoint
       propio, así que ni guardar la ficha, ni cambiarle el rol —que hoy borra la ficha
       `inspector`—, ni una sincronización con SAP pueden borrar ninguna función (I5).
       Va contra `usernameFicha` y no contra el campo del formulario, igual que las otras dos: el
       username se edita letra a letra y, atado al campo, la tarjeta pediría las funciones de
       nombres intermedios, o las de otro usuario que sí exista.
       Le pasamos el `readonly` de la ficha: en /empleados/view/:id/ la ruta entra con
       editable:false, y sin esto un ADMIN podría añadir y finalizar funciones desde la vista de
       «Ver», que es lo que se corrigió en la tarjeta de domicilio (revisión CODEX del PR #1709).
       Y `!soloTarjetaVacaciones`, igual que el r-form de la ficha: a un DIRECTOR sin acceso al
       módulo de empleados esta ruta se le abrió solo para la tarjeta de Vacaciones, y los motivos
       de alta y de baja de las funciones son datos de personal que ahí no le corresponde ver
       (dirección, 03/10/2026). -->
  <rh-funciones-card
    v-if="id && usernameFicha && !soloTarjetaVacaciones"
    ref="funcionesCard"
    :username="usernameFicha"
    :readonly="readonly"
  ></rh-funciones-card>
  <!-- Microsoft 365 (#1044). Fuera del r-form como las demás: tiene su propio endpoint y su
       propio guardado, así que guardar la ficha no pide nada a Microsoft ni cambia permisos, y con
       la integración apagada la ficha funciona igual que antes. Va contra `empleadoIdFicha`, el id
       ya guardado: en el alta aparece en cuanto se guarda la ficha, que es cuando se puede pedir el
       alta en Microsoft. Solo la dibuja quien es responsable de Microsoft 365 (lo decide la API).
       Sin `!soloTarjetaVacaciones` a propósito: su permiso no es el del módulo de empleados, y
       DIRECTOR —responsable de Microsoft 365 sin ese módulo en su menú— llega a esta ficha de
       lectura desde la pantalla «Microsoft 365» para revisar o reintentar. La tarjeta solo enseña
       lo de Microsoft 365; el resto de la ficha sigue colgando de `verFichaCompleta`. -->
  <microsoft365-card
    ref="microsoft365Card"
    :empleadoId="empleadoIdFicha"
    :username="usernameFicha"
  ></microsoft365-card>
  </f7-page>
</template>
<style scoped>
  .label-group {
    margin-top: 12px;
    margin-left: 20px;
    font-weight: bold;
  }
  .label-group-inspeccion {
    margin-top: 30px;
  }
  label.btn {
    padding: 1px;
    cursor: pointer;
    border: 1px solid;
  }
  .aviso-acceso-erp {
    list-style: none;
    padding: 4px 16px 8px;
    font-size: 13px;
    color: var(--f7-text-color, #555);
    opacity: 0.85;
  }

</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rFiles from "./../../components/rFiles.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import rAutocompleteM2m from "./../../components/rAutocompleteM2m.vue";
import rTimepicker from "./../../components/rTimepicker.vue";
import rDireccion from "./../../components/rDireccion.vue";
import VacacionesCard from "./vacaciones-card.vue";
import Empleado from "./../../js/db/Empleado.js";
import RhVacaciones from "./../../js/db/RhVacaciones.js";
import Cliente from "./../../js/db/Cliente.js";
import Empresa from "./../../js/db/Empresa.js";
import LineaNegocio from "./../../js/db/LineaNegocio.js";
import Delegacion from "./../../js/db/Delegacion.js";
import CentroTrabajo from "./../../js/db/CentroTrabajo.js";
import Departamento from "./../../js/db/Departamento.js";
import Documento from "./../../js/db/Documento.js";
import Rol from "./../../js/db/Rol.js";
import Servicio from "./../../js/db/Servicio.js";
import Oficina from "./../../js/db/Oficina.js";
import RhDomicilioCard from "./rh-domicilio-card.vue";
import RhFuncionesCard from "./rh-funciones-card.vue";
import ServiciosHuerfanosAviso from "./servicios-huerfanos-aviso.vue";
import AvisoServiciosMarcados from "./aviso-servicios-marcados.vue";
import Microsoft365Card from "./microsoft365-card.vue";
import PerfilCrmCard from "./perfil-crm-card.vue";

export default {
  components: {
    rForm,
    rInput,
    rTimepicker,
    rFiles,
    rDireccion,
    rAutocomplete,
    rAutocompleteM2m,
    RhDomicilioCard,
    RhFuncionesCard,
    ServiciosHuerfanosAviso,
    AvisoServiciosMarcados,
    Microsoft365Card,
    PerfilCrmCard,
    VacacionesCard
  },
  data: function() {
    var self = this;
    return {
      // La tarjeta de vacaciones va por username, no por id: se conoce al cargar el empleado.
      usernameFicha: null,
      // Y la de Microsoft 365 por id, que en el alta solo existe después de guardar (#1044).
      empleadoIdFicha: this.id || null,
      // Si la ficha tiene usuario de aplicación, que es lo que hace que eliminarla le quite el
      // acceso a la ERP (#932). Sale de lo guardado, no del formulario.
      tieneAccesoErp: false,
      // Avisos del número de servicios marcados que ha contestado el ÚLTIMO guardado (#305). Nacen
      // vacíos: al abrir la ficha no hay nada que avisar porque nadie ha guardado nada todavía.
      avisosServicios: [],
      Empleado,
      Cliente,
      Empresa,
      LineaNegocio,
      Delegacion,
      CentroTrabajo,
      Departamento,
      Rol,
      Servicio,
      Oficina,
      readonly: !this.editable,
      // Username del empleado tal y como está guardado en el servidor (#53). Es lo que come la
      // tarjeta de domicilio; mientras esté vacío, la tarjeta ni se monta.
      Documento
    };
  },
  props: {
    id: {
      type: String
    },
    editable: {
      type: Boolean,
      default: true
    },
    /**
     * Si esta persona tiene acceso al **módulo de empleados**, que es lo que da derecho a ver una
     * ficha de personal entera: correo, teléfono, DNI, firma, altas y bajas y los datos de
     * organización.
     *
     * [#65, 02/10/2026] Llega YA RESUELTO desde la barrera de la ruta
     * (`barreraUsuarioActualizado.js`), con el usuario que ha traído `Usuario.me()` en esta carga.
     * Antes lo resolvía la pantalla y lo revisaba con un temporizador cuando apareciera el menú:
     * el menú guardado en `localStorage` contestaba al instante con el de la sesión anterior, así
     * que la revisión terminaba antes de empezar y el permiso se quedaba fijo toda la sesión.
     *
     * Por defecto `false`: si alguien montara esta pantalla sin pasar por la ruta, lo que no se
     * enseña son los datos personales, no la tarjeta de vacaciones.
     */
    verFichaCompleta: {
      type: Boolean,
      default: false
    },
    /**
     * Si esta persona puede editar o eliminar el empleado: el mismo permiso efectivo que autoriza
     * `/empleados/edit/:id/`, resuelto también en la barrera de la ruta.
     */
    puedeGestionar: {
      type: Boolean,
      default: false
    },
    /**
     * [#932] Si puede asignar o cambiar el rol, dar o quitar el usuario de aplicación y eliminar la
     * ficha de quien lo tiene: solo ADMIN (`cambiarAcceso` de `permisos.js`). Resuelto en la
     * barrera de la ruta, como los otros dos. Por defecto `false`: sin pasar por la ruta, lo que no
     * se ofrece es cambiar el acceso de nadie.
     */
    puedeCambiarAcceso: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /**
     * Quién ve «Editar» y «Eliminar»: quien tiene el permiso efectivo del módulo y además está
     * viendo la ficha. Lo segundo no es redundante: los dos valores salen del mismo permiso, pero
     * ofrecer «Editar» sobre una tarjeta de vacaciones no tendría nada que editar.
     */
    puedeGestionarEmpleado: function() {
      return this.puedeGestionar && !this.soloTarjetaVacaciones;
    },
    /** [#932] Eliminar la ficha de quien tiene usuario de aplicación le quita el acceso: solo ADMIN. */
    puedeEliminar: function() {
      return this.puedeCambiarAcceso || !this.tieneAccesoErp;
    },
    /**
     * [revisión #1711] Quien llega a esta ruta por el **acceso especial de vacaciones** y no por
     * el permiso del módulo —DIRECTOR, que entra desde el Mapa de aprobación— ve la tarjeta y
     * nada más.
     *
     * [JJCS, 01/10/2026] La regla de negocio que separa las dos cosas, escrita: tener acceso a
     * una pantalla no es tener derecho a los datos personales de un compañero. Quién puede ver
     * los datos de la solicitud de vacaciones de otra persona es cada uno la suya, el aprobador y
     * su suplente las de quien les toca aprobar, ADMIN y DIRECTOR todas, y nadie más tenga el
     * permiso de menú que tenga. Eso lo decide la API en cada petición (el gate de
     * `rh/vacaciones/*`), no esta pantalla; aquí solo se decide si además se enseña la ficha de
     * personal, que es lo que cuelga del permiso del módulo.
     */
    soloTarjetaVacaciones: function() {
      return !this.verFichaCompleta;
    }
  },
  methods: {
    // Guardar la tarjeta de «Vacaciones» puede haber movido el check «Gestiona vacaciones en este
    // usuario» a otra cuenta de la misma persona, y con él las funciones vigentes (#51): cambian
    // tanto lo que enseña «Puesto y funciones» como si se puede editar desde aquí.
    //
    // Y «Domicilio y calendario laboral» mira ese mismo check para saber cuál de las cuentas de la
    // persona manda sobre el domicilio (`RhCuentasPersona.porMando`), así que se queda igual de
    // desfasada: enseñando el domicilio de la otra cuenta y diciendo que es esta. Es el mismo
    // olvido que el de la tarjeta de funciones, en la tarjeta de al lado.
    recargarPorCuentaGestora: function() {
      if (this.$refs.funcionesCard) {
        this.$refs.funcionesCard.recargar();
      }
      if (this.$refs.domicilioCard) {
        this.$refs.domicilioCard.recargar('cuentaGestora');
      }
    },
    addToArray(namespace) {
      const self = this;
      var arr = self.$refs.rform.formData[namespace] || [];
      arr.push("");
      self.$set(self.$refs.rform.formData, namespace, arr);
    },
    beforeSaveEmpleado: function(data, resolve, reject) {
      const self = this;
      if (data.firma && typeof data.firma.length != "undefined") {
        data.firma = data.firma[0];
      }
      resolve(data);
    },
    explicarEliminar: function() {
      this.$f7.dialog.alert(this.$t("empleados.acceso.soloAdminEliminar"),
        this.$t("empleados.acceso.tituloEliminar"));
    },
    deleteItem: function() {
      var self = this;
      var app = self.$f7;
      app.dialog.confirm(
        self.$t("rlist.pregunta_eliminar_registro"),
        "Eliminar",
        function() {
          Empleado.delete({itemId: self.id}).then(function(res) {
            if (res) {
              app.toast
                .create({
                  icon: '<i class="f7-icons">checkmark</i>',
                  text: self.$t("rlist.registro_eliminado"),
                  position: "center",
                  destroyOnClose: true,
                  closeTimeout: 2000
                })
                .open();
              self.$f7router.back()
            }
          }).catch(function(error){
            app.dialog.alert(error.message);
          });
        }
      );
    },
    // El empleado ya existe en el servidor con este username: a partir de aquí las tarjetas de
    // «Vacaciones» (#52) y de domicilio (#53) pueden cargarse (en el alta la ruta sigue siendo
    // /empleados/new/ y no hay `id`). Si el guardado ha cambiado el username, se recargan con el
    // nuevo.
    onLoadEmpleado: function(res) {
      this.usernameFicha = (res && res.username) || null;
      this.tieneAccesoErp = !!(res && res.securityUserId);
      this.empleadoIdFicha = (res && res.id) || this.id || null;
    },
    /**
     * De quién es esta ficha, cuando la ficha no se pinta.
     *
     * [revisión #1711] Va por `rh/vacaciones/empleado/{id}`, que devuelve id, usuario y nombre y
     * nada más. Antes se pedía el empleado entero y se usaba solo el username: no se enseñaba el
     * resto, pero correo, teléfono, dni y firma viajaban igual hasta el navegador de quien solo
     * tiene permiso sobre las vacaciones, y ahí se ven desde las herramientas de red.
     */
    cargarUsernameFicha: function() {
      const self = this;
      RhVacaciones.empleado(self.id)
        .then(function(res) {
          self.onLoadEmpleado(res);
          if (!self.usernameFicha) {
            self.$f7.dialog.alert(self.$t("vacaciones.errorCargando"));
          }
        })
        .catch(function() {
          self.$f7.dialog.alert(self.$t("vacaciones.errorCargando"));
        });
    },
    onSave: function(res) {
      var self = this;
      var app = self.$f7;
      var router = self.$f7router;
      self.$refs.rform.formData = res;
      self.readonly = true;
      var usernameAnterior = self.usernameFicha;
      const empleadoIdAnterior = self.empleadoIdFicha;
      // El username se puede cambiar aquí arriba, y las tarjetas van por username: sin esto se
      // quedarían apuntando al anterior y su siguiente guardado iría a otra ficha o daría un 404
      // (revisión CODEX del PR #1707).
      self.onLoadEmpleado(res);
      // La sugerencia de municipio de la tarjeta sale de la dirección de inicio de jornada, que
      // se edita en esta misma ficha. Si el username no ha cambiado, el watcher de la tarjeta no
      // salta y se quedaría ofreciendo el municipio de la dirección anterior, que el usuario
      // podría dar por bueno con «Usar» (revisión CODEX del PR #1709).
      if (usernameAnterior && usernameAnterior === self.usernameFicha && self.$refs.domicilioCard) {
        self.$refs.domicilioCard.recargar();
      }
      // Y el aviso de códigos huérfanos (#329), por lo mismo: la lista de servicios se acaba de
      // guardar desde esta pantalla, así que dejarlo con la foto de antes diría que el cambio no
      // ha surtido efecto —o que sí— sin haberlo mirado. Si el username ha cambiado lo recarga su
      // propio watcher, igual que a las demás tarjetas.
      if (usernameAnterior && usernameAnterior === self.usernameFicha && self.$refs.huerfanosAviso) {
        self.$refs.huerfanosAviso.recargar();
      }
      // Y la de Microsoft 365 (#1044): su rol, su fecha de incorporación, las sugerencias y qué se
      // puede pedir salen de la ficha que se acaba de guardar. Va por id, que no cambia al guardar,
      // así que su watcher no salta; en el alta sí cambia y la carga el watcher.
      if (empleadoIdAnterior && empleadoIdAnterior === self.empleadoIdFicha && self.$refs.microsoft365Card) {
        self.$refs.microsoft365Card.recargar();
      }
      // Y el perfil de CRM (#864): el guardado puede haberlo cambiado, y entonces hay un apunte nuevo
      // en su historial. En el alta lo carga su watcher, porque el id cambia.
      if (empleadoIdAnterior && empleadoIdAnterior === self.empleadoIdFicha && self.$refs.perfilCrmCard) {
        self.$refs.perfilCrmCard.recargar();
      }
      // Y los avisos del número de servicios marcados (#305), que los contesta este mismo guardado.
      // Se asignan siempre, también cuando vienen vacíos: así el aviso del guardado anterior
      // desaparece en cuanto la lista deja de merecerlo, en vez de quedarse contradiciendo a la
      // lista que hay debajo.
      self.avisosServicios = (res && res.avisosServicios) || [];
      app.toast
        .create({
          text: self.$t("empleados.guardado_ok"),
          position: "center",
          closeTimeout: 2000,
          icon: '<i class="f7-icons">checkmark_alt</i>'
        })
        .open();
    }
  },
  created: function() {
    var self = this;
    // [#65] Los permisos llegan como `prop` resueltos en la barrera de la ruta, así que aquí ya no
    // hay nada que revisar ni ningún temporizador que parar: cuando esta pantalla nace, lo que
    // puede enseñar está decidido con el usuario de esta carga.
    //
    // Sin la ficha montada no hay `loadItem` que dé el username, y la tarjeta de vacaciones va
    // por username: se pide el empleado solo para saber cuál es.
    if (self.soloTarjetaVacaciones && self.id && !self.usernameFicha) {
      self.cargarUsernameFicha();
    }
  }
};
</script>